"use client";

import { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useNotificationsPanel } from "@/contexts/NotificationsContext";
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import { useNotifications } from "@/hooks/calendar/useNotifications";
import { useRevisions } from "@/hooks/calendar/useRevisions";
import { useWaitlistMatches } from "@/hooks/calendar/useWaitlistMatches";
import { NotificationUpdate } from "@/lib/types/notifications";
import { RevisionItem } from "@/lib/types/revisions";
import { getAppointmentRegistry } from "@/lib/appointment/appointmentRegistrySingleton";
import { Smartphone, Phone, Trash2, Bell, UserX, Calendar, X, History } from "lucide-react";
import { cn } from "@/lib/utils";
import WaitlistTabContent from "./WaitlistTabContent";

type FilterType = "all" | "archived" | "changes" | "waitlist";


/**
 * Get icon and background color based on notification type_slug
 */
function getNotificationIconConfig(typeSlug: string) {
    switch (typeSlug) {
        case "record_created_online":
            return {
                icon: <Smartphone className="w-4 h-4 text-purple-600" />,
                bg: "bg-purple-100",
            };
        case "record_created_offline":
            return {
                icon: <Phone className="w-4 h-4 text-blue-600" />,
                bg: "bg-blue-100",
            };
        case "record_deleted":
            return {
                icon: <Trash2 className="w-4 h-4 text-red-500" />,
                bg: "bg-red-100",
            };
        case "record_client_did_not_come":
            return {
                icon: <UserX className="w-4 h-4 text-orange-500" />,
                bg: "bg-orange-100",
            };
        default:
            return {
                icon: <Calendar className="w-4 h-4 text-amber-600" />,
                bg: "bg-amber-100",
            };
    }
}

/**
 * Get header text based on notification type_slug (English style like mockup)
 */
function getNotificationHeader(typeSlug: string, t: (key: string, defaultValue: string) => string): string {
    switch (typeSlug) {
        case "record_created_online":
            return t("notifications.type.new_online_booking", "New online booking");
        case "record_created_offline":
            return t("notifications.type.new_booking", "New booking");
        case "record_deleted":
            return t("notifications.type.booking_deleted", "Booking deleted");
        case "record_client_did_not_come":
            return t("notifications.type.client_no_show", "Client did not show");
        default:
            return t("notifications.type.notification", "Notification");
    }
}

/**
 * Format created_at timestamp to HH:mm format
 */
function formatTime(createdAt: string): string {
    try {
        const date = new Date(createdAt);
        return date.toLocaleTimeString("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

/**
 * Format created_at date for grouping
 */
function formatDateGroup(createdAt: string, t: (key: string, defaultValue: string) => string): string {
    try {
        const date = new Date(createdAt);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return t("notifications.date.today", "TODAY");
        }
        if (date.toDateString() === yesterday.toDateString()) {
            return t("notifications.date.yesterday", "YESTERDAY");
        }

        return date.toLocaleDateString("ru-RU", {
            day: "numeric",
            month: "long",
        }).toUpperCase();
    } catch {
        return "";
    }
}

interface NotificationRowProps {
    notification: NotificationUpdate;
}

function NotificationRow({ notification }: NotificationRowProps) {
    const iconConfig = getNotificationIconConfig(notification.type_slug);
    const { t } = useTranslation();

    return (
        <div className="flex items-start gap-3 py-4 px-4 hover:bg-gray-50/50 transition-colors">
            {/* Icon with colored background */}
            <div
                className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center",
                    iconConfig.bg
                )}
            >
                {iconConfig.icon}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-sm text-gray-900">
                        {getNotificationHeader(notification.type_slug, t)}
                    </span>
                    <span className="text-xs text-gray-400 flex-shrink-0 mt-0.5">
                        {formatTime(notification.created_at)}
                    </span>
                </div>
                <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">
                    {notification.text}
                </p>
            </div>
        </div>
    );
}

interface RevisionRowProps {
    revision: RevisionItem;
    onOpenAppointment: (recordId: number) => void;
}

/**
 * Format a label by splitting on commas.
 * First part stays as-is, subsequent parts are indented.
 */
function formatLabelParts(label: string): React.ReactNode {
    const parts = label.split(', ');
    if (parts.length === 1) {
        return label;
    }
    return (
        <>
            {parts[0]}
            {parts.slice(1).map((part, i) => (
                <span key={i} className="block pl-3">{part}</span>
            ))}
        </>
    );
}

function RevisionRow({ revision, onOpenAppointment }: RevisionRowProps) {
    const { t } = useTranslation();
    return (
        <div className="py-3 px-4 hover:bg-gray-50/50 transition-colors">
            {/* Header row: name, open button, time */}
            <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-gray-900">
                        {revision.changed_by.name}
                        <span className="font-normal text-gray-500">
                            {" — "}
                            {revision.changed_by.type === "user" ? t("changes.user_type.admin", "admin") : t("changes.user_type.client", "client")}
                        </span>
                    </span>
                    <button
                        onClick={() => onOpenAppointment(revision.record_id)}
                        className="px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-700 hover:bg-indigo-200 transition-colors"
                    >
                        {t("changes.open_record", "Open")}
                    </button>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">
                    {formatTime(revision.changed_at)}
                </span>
            </div>
            {/* Labels - full text, smaller font, split on commas */}
            <ul className="text-xs text-gray-500 space-y-0.5">
                {revision.labels.map((label, idx) => (
                    <li key={idx}>• {formatLabelParts(label)}</li>
                ))}
            </ul>
        </div>
    );
}



interface GroupedNotifications {
    date: string;
    items: NotificationUpdate[];
}

export function NotificationsPanel() {
    const { isOpen, close, pendingTab, consumePendingTab } = useNotificationsPanel();
    const { openModal } = useAppointmentModal();
    const { data: notifications = [], isLoading: isLoadingNotifications } = useNotifications(isOpen);
    const { data: revisions = [], isLoading: isLoadingRevisions } = useRevisions(isOpen);
    const { data: waitlistData } = useWaitlistMatches(isOpen, { withToasts: false });
    const waitlistMatchCount = waitlistData?.matches?.length ?? 0;
    const [filter, setFilter] = useState<FilterType>("all");
    const { t } = useTranslation();

    // Jump straight to a tab when opened via openTo() (e.g. the waitlist icon).
    useEffect(() => {
        if (isOpen && pendingTab) {
            setFilter(pendingTab);
            consumePendingTab();
        }
    }, [isOpen, pendingTab, consumePendingTab]);

    const handleOpenAppointment = (recordId: number) => {
        const registry = getAppointmentRegistry();
        const appointment = registry.getAppointmentById(String(recordId));
        if (!appointment) {
            alert(t("notifications.appointment_not_found", "Appointment not found or was deleted"));
            return;
        }
        close();
        openModal(recordId);
    };

    const isLoading = filter === "changes" ? isLoadingRevisions : isLoadingNotifications;

    // Filter notifications based on selected pill
    const filteredNotifications = useMemo(() => {
        if (filter === "archived") {
            return notifications.filter((n) => n.type_slug === "record_deleted");
        }
        return notifications;
    }, [notifications, filter]);

    // Group notifications by date
    const groupedNotifications = useMemo(() => {
        const groups: Record<string, NotificationUpdate[]> = {};

        filteredNotifications.forEach((notification) => {
            const dateKey = formatDateGroup(notification.created_at, t);
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(notification);
        });

        return Object.entries(groups).map(([date, items]) => ({
            date,
            items,
        })) as GroupedNotifications[];
    }, [filteredNotifications]);

    return (
        <Sheet open={isOpen} onOpenChange={(open) => !open && close()}>
            <SheetContent
                side="right"
                className="w-full sm:w-[360px] lg:w-[20vw] lg:min-w-[360px] p-0 flex flex-col [&>button]:hidden rounded-l-2xl"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <SheetTitle className="text-xl font-bold text-gray-900">
                        {t("notifications.title", "Notifications")}
                    </SheetTitle>
                    <SheetDescription className="sr-only">
                        {t("notifications.description", "View and manage your notifications")}
                    </SheetDescription>
                    <button
                        onClick={close}
                        className="p-1 rounded-md hover:bg-gray-100 transition-colors"
                        aria-label={t("common.buttons.close", "Close")}
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Top-level folder tabs: Records | Waiting list. "Records"
                    groups the three notification sub-filters. */}
                {(() => {
                    const onRecords = filter !== "waitlist";
                    return (
                        <>
                            <div className="flex items-stretch gap-1 border-b border-gray-200 px-3">
                                <button
                                    onClick={() => { if (filter === "waitlist") setFilter("all"); }}
                                    className={cn(
                                        "flex items-center gap-1 whitespace-nowrap px-2.5 py-2 text-xs font-medium -mb-px border-b-2 transition-colors",
                                        onRecords
                                            ? "border-amber-500 text-amber-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    )}
                                >
                                    {t("sidebar.nav.records", "Records")}
                                </button>
                                <button
                                    onClick={() => setFilter("waitlist")}
                                    className={cn(
                                        "flex items-center gap-1 whitespace-nowrap px-2.5 py-2 text-xs font-medium -mb-px border-b-2 transition-colors",
                                        filter === "waitlist"
                                            ? "border-amber-500 text-amber-600"
                                            : "border-transparent text-gray-500 hover:text-gray-700"
                                    )}
                                >
                                    {t("notifications.filter.waitlist", "Waiting list")}
                                    {waitlistMatchCount > 0 && (
                                        <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-emerald-600 px-1 text-[9px] font-bold text-white">
                                            {waitlistMatchCount}
                                        </span>
                                    )}
                                </button>
                            </div>

                            {/* Records sub-tabs — shown only when Records is active */}
                            {onRecords && (
                                <div className="flex gap-1.5 px-4 py-1.5 bg-gray-50/60 border-b border-gray-100">
                                    {([
                                        { key: "all", label: t("notifications.filter.all", "All") },
                                        { key: "archived", label: t("notifications.filter.deleted", "Deleted") },
                                        { key: "changes", label: t("notifications.filter.changes", "Changes") },
                                    ] as { key: FilterType; label: string }[]).map((sub) => (
                                        <button
                                            key={sub.key}
                                            onClick={() => setFilter(sub.key)}
                                            className={cn(
                                                "rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors",
                                                filter === sub.key
                                                    ? "bg-amber-500 text-white"
                                                    : "text-gray-500 hover:bg-gray-200"
                                            )}
                                        >
                                            {sub.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    );
                })()}

                {/* Content list. min-h-0 is load-bearing: without it a flex
                    child will not shrink below its content height in Safari,
                    so a long list overflows the sheet instead of scrolling. */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                    {filter === "waitlist" ? (
                        <WaitlistTabContent onClose={close} />
                    ) : isLoading ? (
                        <div className="flex items-center justify-center h-32">
                            <div className="animate-spin rounded-full h-6 w-6 border-2 border-amber-500 border-t-transparent" />
                        </div>
                    ) : filter === "changes" ? (
                        // Changes/Revisions view
                        revisions.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                                <History className="w-10 h-10 mb-3 stroke-[1.5]" />
                                <span className="text-sm">{t("notifications.empty_changes", "No changes today")}</span>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {revisions.map((revision) => (
                                    <RevisionRow
                                        key={revision.id}
                                        revision={revision}
                                        onOpenAppointment={handleOpenAppointment}
                                    />
                                ))}
                            </div>
                        )
                    ) : groupedNotifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-40 text-gray-400">
                            <Bell className="w-10 h-10 mb-3 stroke-[1.5]" />
                            <span className="text-sm">{t("notifications.empty", "No notifications")}</span>
                        </div>
                    ) : (
                        <div>
                            {groupedNotifications.map((group, groupIndex) => (
                                <div key={group.date}>
                                    {/* Date separator - only show if not the first "today" group */}
                                    {(groupIndex > 0 || group.date !== t("notifications.date.today", "TODAY")) && (
                                        <div className="px-5 py-2 mt-2">
                                            <span className="text-xs font-medium text-gray-400 tracking-wide">
                                                {group.date}
                                            </span>
                                        </div>
                                    )}
                                    {/* Notification items */}
                                    <div className="divide-y divide-gray-100">
                                        {group.items.map((notification) => (
                                            <NotificationRow
                                                key={notification.id}
                                                notification={notification}
                                            />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
